import {
  trafficLevelSchema,
  type RunConditions,
  type RunConditionsFormValues,
  type RunDetail,
  type RunMeasurements,
  type RunMeasurementsFormValues,
  type Scenario,
  type TirePressureSet,
  type Vehicle,
} from 'fuel-benchmark-contracts';

/** The traffic levels the wizard offers, straight from the contract so the two can never drift. */
export const TRAFFIC_LEVELS = trafficLevelSchema.options;

/**
 * One description of every condition and measurement field, shared by the wizard forms, the run detail
 * definition lists and the review step. Labels, units and grouping therefore cannot drift apart, and a new
 * field is added in exactly one place.
 */
export interface RunFieldDescriptor<TValues> {
  name: keyof TValues & string;
  labelKey: string;
  kind: 'number' | 'text' | 'boolean' | 'select';
  /**
   * The values a `select` field offers, in the order they are listed. Their labels — and the empty
   * "not recorded" option's — come from `optionLabelPrefix`, so the words stay translatable while the
   * stored value stays the domain enum.
   */
  options?: readonly string[];
  optionLabelPrefix?: string;
  /** Appended when the value is displayed, and shown as the input's suffix hint. */
  unit?: string;
  step?: string;
  /** Rendered only for a hybrid or plug-in hybrid vehicle. */
  hybridOnly?: boolean;
  /** Rendered only for a DRIVE run — an idle test has no route and no distance. */
  driveOnly?: boolean;
}

export interface RunFieldGroup<TValues> {
  titleKey: string;
  fields: readonly RunFieldDescriptor<TValues>[];
}

type ConditionField = RunFieldDescriptor<RunConditionsFormValues>;
type MeasurementField = RunFieldDescriptor<RunMeasurementsFormValues>;

export const CONDITION_GROUPS: readonly RunFieldGroup<RunConditionsFormValues>[] = [
  {
    titleKey: 'runs.groups.vehicleConfig',
    fields: [
      { name: 'driveMode', labelKey: 'runs.fields.driveMode', kind: 'text' },
      // Traffic belongs to the session, not to the route: the same circuit is a different test at 08:00.
      {
        name: 'trafficLevel',
        labelKey: 'runs.fields.trafficLevel',
        kind: 'select',
        options: TRAFFIC_LEVELS,
        optionLabelPrefix: 'enums.trafficLevel',
      },
      { name: 'odometerKm', labelKey: 'runs.fields.odometerKm', kind: 'number', unit: 'km' },
      { name: 'warningLightsPresent', labelKey: 'runs.fields.warningLightsPresent', kind: 'boolean' },
    ] satisfies ConditionField[],
  },
  {
    titleKey: 'runs.groups.tires',
    fields: [
      { name: 'tireMake', labelKey: 'runs.fields.tireMake', kind: 'text' },
      { name: 'tireModel', labelKey: 'runs.fields.tireModel', kind: 'text' },
      { name: 'pressureFlKpa', labelKey: 'runs.fields.pressureFlKpa', kind: 'number', unit: 'kPa' },
      { name: 'pressureFrKpa', labelKey: 'runs.fields.pressureFrKpa', kind: 'number', unit: 'kPa' },
      { name: 'pressureRlKpa', labelKey: 'runs.fields.pressureRlKpa', kind: 'number', unit: 'kPa' },
      { name: 'pressureRrKpa', labelKey: 'runs.fields.pressureRrKpa', kind: 'number', unit: 'kPa' },
    ] satisfies ConditionField[],
  },
  {
    titleKey: 'runs.groups.payload',
    fields: [
      { name: 'driverWeightKg', labelKey: 'runs.fields.driverWeightKg', kind: 'number', unit: 'kg' },
      { name: 'passengerCount', labelKey: 'runs.fields.passengerCount', kind: 'number' },
      { name: 'passengerWeightKg', labelKey: 'runs.fields.passengerWeightKg', kind: 'number', unit: 'kg' },
      { name: 'ballastWeightKg', labelKey: 'runs.fields.ballastWeightKg', kind: 'number', unit: 'kg' },
      { name: 'totalPayloadKg', labelKey: 'runs.fields.totalPayloadKg', kind: 'number', unit: 'kg' },
    ] satisfies ConditionField[],
  },
  {
    titleKey: 'runs.groups.fuel',
    fields: [
      { name: 'fuelGrade', labelKey: 'runs.fields.fuelGrade', kind: 'text' },
      { name: 'fuelSource', labelKey: 'runs.fields.fuelSource', kind: 'text' },
      { name: 'startingFuelCondition', labelKey: 'runs.fields.startingFuelCondition', kind: 'text' },
      { name: 'fuelMeasurementMethod', labelKey: 'runs.fields.fuelMeasurementMethod', kind: 'text' },
    ] satisfies ConditionField[],
  },
  {
    titleKey: 'runs.groups.hvac',
    fields: [
      { name: 'hvacSetpointC', labelKey: 'runs.fields.hvacSetpointC', kind: 'number', unit: '°C', step: '0.5' },
      { name: 'fanLevel', labelKey: 'runs.fields.fanLevel', kind: 'number' },
      { name: 'recirculation', labelKey: 'runs.fields.recirculation', kind: 'boolean' },
      { name: 'windowsState', labelKey: 'runs.fields.windowsState', kind: 'text' },
    ] satisfies ConditionField[],
  },
  {
    titleKey: 'runs.groups.thermal',
    fields: [
      { name: 'engineCoolantTempC', labelKey: 'runs.fields.engineCoolantTempC', kind: 'number', unit: '°C', step: '0.1' },
      { name: 'cabinTempC', labelKey: 'runs.fields.cabinTempC', kind: 'number', unit: '°C', step: '0.1' },
      { name: 'soakDurationMin', labelKey: 'runs.fields.soakDurationMin', kind: 'number', unit: 'min' },
      { name: 'hybridSocStartPct', labelKey: 'runs.fields.hybridSocStartPct', kind: 'number', unit: '%', step: '0.1', hybridOnly: true },
    ] satisfies ConditionField[],
  },
];

export const MEASUREMENT_GROUPS: readonly RunFieldGroup<RunMeasurementsFormValues>[] = [
  {
    titleKey: 'runs.groups.environment',
    fields: [
      { name: 'ambientTempAvgC', labelKey: 'runs.fields.ambientTempAvgC', kind: 'number', unit: '°C', step: '0.1' },
      { name: 'ambientTempStartC', labelKey: 'runs.fields.ambientTempStartC', kind: 'number', unit: '°C', step: '0.1' },
      { name: 'ambientTempEndC', labelKey: 'runs.fields.ambientTempEndC', kind: 'number', unit: '°C', step: '0.1' },
      { name: 'relativeHumidityAvgPct', labelKey: 'runs.fields.relativeHumidityAvgPct', kind: 'number', unit: '%', step: '0.1' },
      { name: 'windSpeedAvgKph', labelKey: 'runs.fields.windSpeedAvgKph', kind: 'number', unit: 'kph', step: '0.1' },
      { name: 'roadSurface', labelKey: 'runs.fields.roadSurface', kind: 'text' },
      { name: 'precipitation', labelKey: 'runs.fields.precipitation', kind: 'text' },
    ] satisfies MeasurementField[],
  },
  {
    titleKey: 'runs.groups.motion',
    fields: [
      { name: 'distanceTotalKm', labelKey: 'runs.fields.distanceTotalKm', kind: 'number', unit: 'km', step: '0.001', driveOnly: true },
      { name: 'movingTimeSec', labelKey: 'runs.fields.movingTimeSec', kind: 'number', unit: 's', driveOnly: true },
      { name: 'stationaryPct', labelKey: 'runs.fields.stationaryPct', kind: 'number', unit: '%', step: '0.1', driveOnly: true },
      { name: 'avgSpeedKph', labelKey: 'runs.fields.avgSpeedKph', kind: 'number', unit: 'kph', step: '0.1', driveOnly: true },
      { name: 'maxSpeedKph', labelKey: 'runs.fields.maxSpeedKph', kind: 'number', unit: 'kph', step: '0.1', driveOnly: true },
      { name: 'stopCount', labelKey: 'runs.fields.stopCount', kind: 'number', driveOnly: true },
      { name: 'elevationGainM', labelKey: 'runs.fields.elevationGainM', kind: 'number', unit: 'm', driveOnly: true },
      { name: 'elevationLossM', labelKey: 'runs.fields.elevationLossM', kind: 'number', unit: 'm', driveOnly: true },
    ] satisfies MeasurementField[],
  },
  {
    titleKey: 'runs.groups.fuelUsed',
    fields: [
      { name: 'fuelConsumedL', labelKey: 'runs.fields.fuelConsumedL', kind: 'number', unit: 'L', step: '0.001' },
      { name: 'durationTotalSec', labelKey: 'runs.fields.durationTotalSec', kind: 'number', unit: 's' },
      { name: 'engineOffTimeSec', labelKey: 'runs.fields.engineOffTimeSec', kind: 'number', unit: 's' },
      { name: 'engineRpmAvg', labelKey: 'runs.fields.engineRpmAvg', kind: 'number', unit: 'rpm' },
    ] satisfies MeasurementField[],
  },
  {
    titleKey: 'runs.groups.hybrid',
    fields: [
      { name: 'hybridSocEndPct', labelKey: 'runs.fields.hybridSocEndPct', kind: 'number', unit: '%', step: '0.1', hybridOnly: true },
      { name: 'evTimeSec', labelKey: 'runs.fields.evTimeSec', kind: 'number', unit: 's', hybridOnly: true },
      { name: 'evDistanceKm', labelKey: 'runs.fields.evDistanceKm', kind: 'number', unit: 'km', step: '0.001', hybridOnly: true },
    ] satisfies MeasurementField[],
  },
];

export interface FieldVisibility {
  isHybrid: boolean;
  isDrive: boolean;
}

/** A hybrid-only field on a petrol vehicle, or a route field on an idle test, is noise — it is not rendered. */
export const isFieldVisible = <TValues>(field: RunFieldDescriptor<TValues>, { isHybrid, isDrive }: FieldVisibility): boolean =>
  (!field.hybridOnly || isHybrid) && (!field.driveOnly || isDrive);

export const visibleGroups = <TValues>(
  groups: readonly RunFieldGroup<TValues>[],
  visibility: FieldVisibility,
): { titleKey: string; fields: RunFieldDescriptor<TValues>[] }[] =>
  groups
    .map((group) => ({ titleKey: group.titleKey, fields: group.fields.filter((field) => isFieldVisible(field, visibility)) }))
    .filter((group) => group.fields.length > 0);

export const isHybridPowertrain = (powertrainType: Vehicle['powertrainType']): boolean => powertrainType === 'HYBRID' || powertrainType === 'PHEV';

/**
 * The pre-test checklist starts from what the catalogue already knows: the vehicle's placard pressures, its
 * default fuel grade and odometer, and the HVAC state and traffic level the scenario prescribes. Anything the
 * engineer measured differently is then typed over the top.
 */
export const prefillConditions = (vehicle: Vehicle, scenario: Pick<Scenario, 'hvacState' | 'trafficLevel'>): RunConditionsFormValues => {
  const pressures: TirePressureSet | null = vehicle.oemTirePressureKpa;
  const acOn = scenario.hvacState === 'AC_ON';

  return {
    driveMode: undefined,
    // The traffic the scenario is defined for, offered as the expected reading. It stays editable: the
    // scenario says what the test was meant to meet, the run records what it actually met, and the
    // `traffic_matches_scenario` rule warns when the two disagree.
    trafficLevel: scenario.trafficLevel ?? undefined,
    odometerKm: vehicle.odometerKm ?? undefined,
    warningLightsPresent: false,
    tireMake: undefined,
    tireModel: undefined,
    pressureFlKpa: pressures?.fl,
    pressureFrKpa: pressures?.fr,
    pressureRlKpa: pressures?.rl,
    pressureRrKpa: pressures?.rr,
    fuelGrade: vehicle.defaultFuelGrade ?? undefined,
    // 22 °C with the fan at 5 is the standard cabin setting of the executive brief; A/C off means neither.
    hvacSetpointC: acOn ? 22 : undefined,
    fanLevel: acOn ? 5 : 0,
    recirculation: acOn,
    windowsState: undefined,
  };
};

/**
 * The wizard shows the whole record, so a PATCH has to say "this field is empty" rather than "leave it alone" —
 * an emptied input sends an explicit `null`. Fields that are not rendered at all (hybrid fields on a petrol
 * vehicle, route fields on an idle test) are never registered, so they stay absent and untouched.
 */
export const withExplicitNulls = <TValues extends object>(values: TValues): TValues =>
  Object.fromEntries(Object.entries(values).map(([key, value]) => [key, value === undefined ? null : value])) as TValues;

const orUndefined = <TValue>(value: TValue | null): TValue | undefined => (value === null ? undefined : value);

/** Seeds the conditions form when a draft is resumed from the runs list. */
export const toConditionsFormValues = (conditions: RunConditions): RunConditionsFormValues => ({
  driveMode: orUndefined(conditions.driveMode),
  trafficLevel: orUndefined(conditions.trafficLevel),
  odometerKm: orUndefined(conditions.odometerKm),
  warningLightsPresent: orUndefined(conditions.warningLightsPresent),
  tireMake: orUndefined(conditions.tireMake),
  tireModel: orUndefined(conditions.tireModel),
  pressureFlKpa: orUndefined(conditions.pressureFlKpa),
  pressureFrKpa: orUndefined(conditions.pressureFrKpa),
  pressureRlKpa: orUndefined(conditions.pressureRlKpa),
  pressureRrKpa: orUndefined(conditions.pressureRrKpa),
  driverWeightKg: orUndefined(conditions.driverWeightKg),
  passengerCount: orUndefined(conditions.passengerCount),
  passengerWeightKg: orUndefined(conditions.passengerWeightKg),
  ballastWeightKg: orUndefined(conditions.ballastWeightKg),
  totalPayloadKg: orUndefined(conditions.totalPayloadKg),
  fuelGrade: orUndefined(conditions.fuelGrade),
  fuelSource: orUndefined(conditions.fuelSource),
  startingFuelCondition: orUndefined(conditions.startingFuelCondition),
  fuelMeasurementMethod: orUndefined(conditions.fuelMeasurementMethod),
  hvacSetpointC: orUndefined(conditions.hvacSetpointC),
  fanLevel: orUndefined(conditions.fanLevel),
  recirculation: orUndefined(conditions.recirculation),
  windowsState: orUndefined(conditions.windowsState),
  engineCoolantTempC: orUndefined(conditions.engineCoolantTempC),
  cabinTempC: orUndefined(conditions.cabinTempC),
  hybridSocStartPct: orUndefined(conditions.hybridSocStartPct),
  soakDurationMin: orUndefined(conditions.soakDurationMin),
});

export const toMeasurementsFormValues = (measurements: RunMeasurements): RunMeasurementsFormValues => ({
  ambientTempStartC: orUndefined(measurements.ambientTempStartC),
  ambientTempEndC: orUndefined(measurements.ambientTempEndC),
  ambientTempAvgC: orUndefined(measurements.ambientTempAvgC),
  relativeHumidityAvgPct: orUndefined(measurements.relativeHumidityAvgPct),
  windSpeedAvgKph: orUndefined(measurements.windSpeedAvgKph),
  roadSurface: orUndefined(measurements.roadSurface),
  precipitation: orUndefined(measurements.precipitation),
  distanceTotalKm: orUndefined(measurements.distanceTotalKm),
  durationTotalSec: orUndefined(measurements.durationTotalSec),
  movingTimeSec: orUndefined(measurements.movingTimeSec),
  stationaryPct: orUndefined(measurements.stationaryPct),
  avgSpeedKph: orUndefined(measurements.avgSpeedKph),
  maxSpeedKph: orUndefined(measurements.maxSpeedKph),
  stopCount: orUndefined(measurements.stopCount),
  elevationGainM: orUndefined(measurements.elevationGainM),
  elevationLossM: orUndefined(measurements.elevationLossM),
  fuelConsumedL: orUndefined(measurements.fuelConsumedL),
  engineOffTimeSec: orUndefined(measurements.engineOffTimeSec),
  engineRpmAvg: orUndefined(measurements.engineRpmAvg),
  hybridSocEndPct: orUndefined(measurements.hybridSocEndPct),
  evTimeSec: orUndefined(measurements.evTimeSec),
  evDistanceKm: orUndefined(measurements.evDistanceKm),
});

/** The session half of a draft — collected before the draft is created, since a run's identity never changes. */
export interface RunSessionValues {
  testDate: string;
  startTime?: string;
  endTime?: string;
  driverName?: string;
  operatorName?: string;
  campaignTag?: string;
  notes?: string;
}

export const toSessionValues = (run: RunDetail): RunSessionValues => ({
  testDate: run.testDate,
  startTime: run.startTime === null ? undefined : run.startTime.slice(0, 5),
  endTime: run.endTime === null ? undefined : run.endTime.slice(0, 5),
  driverName: orUndefined(run.driverName),
  operatorName: orUndefined(run.operatorName),
  campaignTag: orUndefined(run.campaignTag),
  notes: orUndefined(run.notes),
});
